/**
 * Reads the download filename out of a `Content-Disposition` header, honouring
 * the RFC 5987 `filename*` form when present. Returns null when the header is
 * absent or unparseable so the caller can fall back to its own name.
 *
 * Note the server must also send `Access-Control-Expose-Headers:
 * Content-Disposition`, or this header is invisible to the browser on a
 * cross-origin response and every download silently takes the fallback name.
 */
export function parseContentDispositionFilename(
  contentDisposition: string | undefined
): string | null {
  if (contentDisposition === undefined) {
    return null
  }
  const filenameStarMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;\s]+)/i
  )
  if (filenameStarMatch?.[1] !== undefined) {
    return decodeURIComponent(filenameStarMatch[1])
  }
  const filenameMatch = contentDisposition.match(
    /filename="([^"]+)"|filename=([^;\s]+)/i
  )
  if (filenameMatch === null) {
    return null
  }
  return filenameMatch[1] ?? filenameMatch[2] ?? null
}

/*
 * Delay before releasing the object URL. Long enough for any browser to have
 * committed the save, short enough that the blob is not pinned in memory —
 * which matters here because an invoice archive can be hundreds of MB.
 */
const OBJECT_URL_RELEASE_DELAY_MS = 60_000

/**
 * Saves an already-fetched Blob to disk under `fileName`.
 *
 * Unlike a direct link to a presigned S3 URL, the `download` attribute is
 * honoured here because the object URL is same-origin — which is what lets the
 * server-chosen filename survive.
 *
 * The URL is revoked on a timer rather than immediately after `click()`.
 * Revoking synchronously is spec-safe and is what smaller CSV downloads
 * elsewhere in this app do, but a ZIP of a whole billing period is orders of
 * magnitude larger, and Safari in particular has cancelled such saves when the
 * URL disappears mid-read. A silent failure here is expensive: the success
 * toast fires and no file appears.
 */
export function saveBlobToDisk(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_RELEASE_DELAY_MS)
}
